import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn.ts';
import { Icon } from './Icon.tsx';

const NativeButton = 'button';

const buttonVariants = cva(
    'focus-ring inline-flex items-center justify-center gap-2 transition-all',
    {
        variants: {
            variant: {
                primary: 'bg-[var(--primary)] font-bold text-[color:var(--primary-content)] shadow-sm hover:shadow-md hover:brightness-110',
                secondary: 'bg-gray-100 font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700',
                ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800',
                danger: 'bg-red-500 font-bold text-white shadow-sm hover:bg-red-600',
                'danger-subtle': 'bg-red-50 font-medium text-red-600 hover:bg-red-100 dark:bg-red-900/10 dark:text-red-400 dark:hover:bg-red-900/30',
                subtle: 'bg-transparent text-gray-500 hover:bg-primary/10 hover:text-primary',
                outline: 'border border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-content',
                blue: 'bg-blue-600 font-bold text-white shadow-blue-500/30 shadow-sm hover:bg-blue-700',
                purple: 'bg-purple-600 font-bold text-white shadow-purple-500/30 shadow-sm hover:bg-purple-700',
                green: 'bg-emerald-600 font-bold text-white shadow-emerald-500/30 shadow-sm hover:bg-emerald-700',
                link: 'bg-transparent font-medium text-primary shadow-none hover:text-primary/80 hover:underline',
                plain: 'bg-transparent font-normal text-inherit shadow-none hover:bg-transparent',
                chip: 'rounded-full bg-gray-100 font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
                'chip-blue': 'rounded-full bg-blue-100 font-medium text-blue-700 hover:ring-2 hover:ring-primary/30 dark:bg-blue-900/30 dark:text-blue-400',
                'chip-amber': 'rounded-full bg-amber-100 font-medium text-amber-700 hover:ring-2 hover:ring-primary/30 dark:bg-amber-900/30 dark:text-amber-400',
                'chip-emerald': 'rounded-full bg-emerald-100 font-medium text-emerald-700 hover:ring-2 hover:ring-primary/30 dark:bg-emerald-900/30 dark:text-emerald-400',
                'chip-purple': 'rounded-full bg-purple-100 font-medium text-purple-700 hover:ring-2 hover:ring-primary/30 dark:bg-purple-900/30 dark:text-purple-400',
                'chip-red': 'rounded-full bg-red-100 font-medium text-red-700 hover:ring-2 hover:ring-primary/30 dark:bg-red-900/30 dark:text-red-400',
            },
            size: {
                none: 'min-h-0 rounded-none p-0 text-sm',
                xs: 'min-h-[28px] rounded-md px-2 py-1 text-xs',
                sm: 'min-h-[32px] rounded-md px-3 py-1.5 text-xs',
                md: 'min-h-[40px] rounded-lg px-4 py-2 text-sm',
                lg: 'min-h-[48px] rounded-lg px-6 py-3 text-base',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'md',
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    ref?: React.Ref<HTMLButtonElement>;
    leftIcon?: string;
    rightIcon?: string;
    isLoading?: boolean;
    children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({ 
    ref,
    variant,
    size,
    className,
    disabled,
    type = 'button',
    leftIcon,
    rightIcon,
    isLoading,
    children, 
    ...props 
}) => (
        <NativeButton
            ref={ref}
            className={cn(
                buttonVariants({ variant, size }),
                (disabled || isLoading) && 'cursor-not-allowed opacity-50',
                className
            )}
            disabled={disabled || isLoading}
            type={type}
            {...props}
        >
            {isLoading ? (
                <Icon name="progress_activity" size="sm" className="animate-spin" />
            ) : (
                leftIcon && <Icon name={leftIcon} />
            )}
            {children}
            {!isLoading && rightIcon ? <Icon name={rightIcon} /> : null}
        </NativeButton>
    );

Button.displayName = 'Button';
